import { ReactNode, useState } from 'react';
import Slider from 'react-slick';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { ReactSVG } from 'react-svg';
import { useTheme } from '@forma/forma-ui-theme';
import { Button } from '@forma/forma-ui-kit';
import Link from '@/components/Link';
import PageBlock from '@/components/PageBlock';
import MarketplaceBlock from '../MarketplaceBlock';
import MarketplaceFaq from '../MarketplaceFaq';

import styles from './marketplace-info.module.css';

interface MarketplaceInfoProps {
  icon: string,
  title: string,
  subtitle: string,
  description: string,
  videos?: string[],
  images: string[],
  faq: { question: string; answer: string }[],
  tariffs?: ReactNode,
  conditions?: ReactNode,
  prices: ReactNode,
}

const MarketplaceInfo = ({ icon, title, subtitle, description, images, faq, videos, tariffs, conditions, prices }: MarketplaceInfoProps) => {
  const { t } = useTranslation();
  const { viewport } = useTheme();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<'info' | 'tariffs' | 'conditions'>('info');

  const isPhone = ['phone', 'tabletS'].includes(viewport);

  return (
    <PageBlock className={styles.root} contentClassName={styles.container}>
      <div className={styles.content}>
        <Button
          viewStyle="secondary"
          onClick={() => router.back()}
          icon={<ReactSVG src="/icons/chevron-left-small.svg" />}
          className={styles.backButton}
          size="m"
        >
          {t('go_back')}
        </Button>

        <div className={styles.head}>
          <div className={styles.headRow}>
            <img className={styles.headIcon} src={icon} alt={title} />
            <div className={styles.headContent}>
              <div className={styles.crumbs}>
                <Link href="/marketplace">Маркетплейс</Link> / <span>{title}</span>
              </div>
              <h1 className={styles.title}>{title}</h1>
              {!isPhone && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
          </div>
          {isPhone && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        {isPhone && (
          <div className={styles.side}>
            {prices}
          </div>
        )}

        <div className={styles.tabs}>
          <Button
            viewStyle={activeTab === 'info' ? 'textPrimary' : 'text'}
            onClick={() => setActiveTab('info')}
            className={classNames(styles.tabButton, activeTab === 'info' && styles.active)}
          >
            Информация
          </Button>
          {tariffs && (
            <Button
              viewStyle={activeTab === 'tariffs' ? 'textPrimary' : 'text'}
              onClick={() => setActiveTab('tariffs')}
              className={classNames(styles.tabButton, activeTab === 'tariffs' && styles.active)}
            >
              Тарифы
            </Button>
          )}
          {conditions && (
            <Button
              viewStyle={activeTab === 'conditions' ? 'textPrimary' : 'text'}
              onClick={() => setActiveTab('conditions')}
              className={classNames(styles.tabButton, activeTab === 'conditions' && styles.active)}
            >
              Условия
            </Button>
          )}
        </div>

        {activeTab === 'info' && (
          <div className={styles.tabContent}>
            <Slider className={styles.slider} slidesToShow={isPhone ? 1 : 2} infinite={false} arrows>
              {videos?.map(video => (
                <div className={styles.sliderItem} key={video}>
                  <iframe
                    title="video"
                    src="https://vkvideo.ru/video_ext.php?oid=-235608466&id=456239018&hd=4"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock;"
                    allowFullScreen
                  />
                </div>
              ))}
              {images.map(image => (
                <div className={styles.sliderItem} key={image}>
                  <img src={image} style={{ width: '100%' }} alt="" />
                </div>
              ))}
            </Slider>
            <MarketplaceBlock title="О виджете">
              <div dangerouslySetInnerHTML={{ __html: description }} />
            </MarketplaceBlock>
            <MarketplaceBlock title="Ответы на частые вопросы" className={styles.faq}>
              <MarketplaceFaq items={faq} />
            </MarketplaceBlock>
          </div>
        )}
        {activeTab === 'tariffs' && tariffs}
        {activeTab === 'conditions' && conditions}
      </div>
      {!isPhone && (
        <div className={styles.side}>
          {prices}
        </div>
      )}
    </PageBlock>
  );
};

export default MarketplaceInfo;
